import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import Lightbox from 'yet-another-react-lightbox';
import type {
  RenderSlideProps,
  SlideImage,
  ViewCallbackProps,
} from 'yet-another-react-lightbox';
import * as Collapsible from '@radix-ui/react-collapsible';
import clsx from 'clsx';
import { SVGIcon } from '../SVGIcon/SVGIcon';
import {
  PhotoGalleryProps,
  PhotoGalleryIconConfig,
} from './PhotoGallery.types';

import 'yet-another-react-lightbox/styles.css';
import { Button } from '../Button/Button';
import useWindowSize from '@/hooks/use-window-size';
import type { IconName } from '../SVGIcon/SVGIcon.types';
import { AuthorizedImage } from '../AuthorizedImage';

interface GallerySlide extends SlideImage {
  photo: PhotoGalleryProps['photos'][number];
}

interface LightboxGalleryImageProps {
  gallerySlide: GallerySlide;
  isActive: boolean;
}

const LightboxGalleryImage: React.FC<LightboxGalleryImageProps> = ({
  gallerySlide,
  isActive,
}) => {
  useEffect(() => {
    if (isActive) {
      gallerySlide.photo.requestFullSize?.();
    }
  }, [gallerySlide.photo, isActive]);

  return (
    <AuthorizedImage
      src={gallerySlide.src}
      alt={typeof gallerySlide.alt === 'string' ? gallerySlide.alt : ''}
      className="max-h-full max-w-full object-contain"
      skeletonClassName="w-[60vw] h-[60vh] bg-white/10 animate-pulse rounded-2xl"
      loading={isActive ? 'eager' : 'lazy'}
      loadPriority={isActive ? 'high' : 'normal'}
      onError={() => gallerySlide.photo.refreshVariant?.('LARGE')}
    />
  );
};

interface GalleryIconProps {
  iconConfig?: PhotoGalleryIconConfig;
  iconName?: IconName;
  galleryVariant: 'damage-hsp' | 'default';
  photosCount: number;
}

const GalleryIcon: React.FC<GalleryIconProps> = ({
  iconConfig,
  iconName,
  galleryVariant,
  photosCount,
}) => {
  // Jeśli podano pełną konfigurację, użyj jej
  if (iconConfig) {
    const {
      name,
      size = 28,
      fill = 'white',
      wrapperBgColor = 'bg-blue',
      wrapperSize = 62,
      wrapperClassName,
      showBadge = false,
      badgeBgColor = 'bg-text-primary',
      badgeTextColor = 'text-grayBorder',
    } = iconConfig;

    // Sprawdź czy wrapperBgColor to klasa Tailwind czy wartość CSS
    const isTailwindClass = wrapperBgColor.startsWith('bg-');
    const wrapperStyle = isTailwindClass
      ? {}
      : { backgroundColor: wrapperBgColor };

    return (
      <div
        className={clsx(
          'relative hidden items-center justify-center rounded-[10px] lg:flex',
          isTailwindClass && wrapperBgColor,
          wrapperClassName,
        )}
        style={{
          width: wrapperSize,
          height: wrapperSize,
          ...wrapperStyle,
        }}
      >
        <SVGIcon name={name} width={size} height={size} fill={fill} />
        {showBadge && (
          <span
            className={clsx(
              'absolute right-[-12px] top-[-8px] flex h-6 w-6 items-center justify-center rounded-full text-sm font-semibold leading-none',
              badgeBgColor,
              badgeTextColor,
            )}
          >
            {photosCount}
          </span>
        )}
      </div>
    );
  }

  // Kompatybilność wsteczna - stara logika
  const resolvedIconName =
    iconName ||
    (galleryVariant === 'damage-hsp' ? 'main-icon' : 'grid-squares');
  const resolvedFill = 'blue';
  const resolvedBgColor = 'bg-feedback-iconBg';

  return (
    <div
      className={clsx(
        'relative h-[62px] w-[62px] items-center justify-center rounded-[10px]',
        galleryVariant === 'damage-hsp' ? 'hidden lg:flex' : 'flex',
        resolvedBgColor,
      )}
    >
      <SVGIcon
        name={resolvedIconName}
        width={28}
        height={28}
        fill={resolvedFill}
      />
      <span className="absolute right-[-12px] top-[-8px] flex h-6 w-6 items-center justify-center rounded-full bg-text-primary text-sm font-semibold leading-none text-grayBorder">
        {photosCount}
      </span>
    </div>
  );
};

export const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  galleryVariant = 'default',
  title,
  photos,
  className,
  onPhotoClick,
  disableLightbox = false,
  showIcon = true,
  iconName,
  iconConfig,
  collapsible = false,
  defaultExpanded = false,
  previewCount = 4,
  onExpandChange,
  i18n,
}) => {
  // i18n fallbacks
  const closeGalleryText = i18n?.closeGalleryText || 'Zamknij galerię';
  const openGalleryText = i18n?.openGalleryText || 'Zobacz galerię';
  const [lightboxIndex, setLightboxIndex] = useState<number>(-1);
  const [isExpanded, setIsExpanded] = useState<boolean>(
    collapsible ? defaultExpanded : true,
  );
  const isPdfMode =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('pdf') === 'true';

  // Dodaj ref do Collapsible.Content
  const contentRef = useRef<HTMLDivElement>(null);
  // Ref do głównego kontenera galerii
  const galleryRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useWindowSize();

  // Lightbox renderujemy własną funkcją (`render.slide` poniżej), żeby móc
  // załadować pełen rozmiar (`fullSrc`) przez `AuthorizedImage` — biblioteka
  // domyślnie ustawiłaby `<img src>` bez nagłówka `Authorization`.
  const lightboxSlides = useMemo<GallerySlide[]>(
    () =>
      photos.map((photo) => ({
        src: photo.fullSrc ?? photo.src,
        alt: photo.alt,
        photo,
        title: photo.title,
      })),
    [photos],
  );

  const renderLightboxSlide = useCallback(
    ({ slide, offset }: RenderSlideProps) => {
      const isActiveSlide = offset === 0;
      const gallerySlide = slide as GallerySlide;

      return (
        <LightboxGalleryImage
          gallerySlide={gallerySlide}
          isActive={isActiveSlide}
        />
      );
    },
    [],
  );

  // Domyślna responsywna konfiguracja kolumn
  // Usuń defaultColumns useCallback

  const handlePhotoClick = useCallback(
    ({ index }: { index: number }) => {
      // Otwieramy lightbox tylko jeśli nie jest wyłączony
      if (!disableLightbox) {
        photos[index]?.requestFullSize?.();
        setLightboxIndex(index);
      }

      // Dodatkowo wywołujemy callback jeśli jest podany
      if (onPhotoClick) {
        onPhotoClick(photos[index], index);
      }
    },
    [onPhotoClick, photos, disableLightbox],
  );

  const handleCloseLightbox = useCallback(() => {
    setLightboxIndex(-1);
  }, []);

  const handleLightboxView = useCallback(({ index }: ViewCallbackProps) => {
    setLightboxIndex(index);
  }, []);

  const handleExpandChange = useCallback(
    (expanded: boolean) => {
      // Jeśli galeria się zamyka, sprawdź pozycję scrolla
      if (!expanded && galleryRef.current) {
        const galleryRect = galleryRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const scrollY = window.scrollY;
        const documentHeight = document.documentElement.scrollHeight;

        // Sprawdź czy użytkownik jest w dolnej części strony lub galerii
        // 1. Czy dolna część galerii jest widoczna (w ostatnich 40% wysokości widoku)
        const isGalleryBottomVisible = galleryRect.bottom > windowHeight * 0.6;

        // 2. Czy użytkownik jest w dolnej części strony (w ostatnich 30% wysokości dokumentu)
        const isNearPageBottom = scrollY + windowHeight > documentHeight * 0.7;

        // 3. Czy dolna część galerii jest poniżej środka widoku
        const isGalleryBelowCenter = galleryRect.top < windowHeight * 0.5;

        // Jeśli którykolwiek warunek jest spełniony, przewiń do góry galerii
        if (
          isGalleryBottomVisible ||
          isNearPageBottom ||
          isGalleryBelowCenter
        ) {
          // Użyj setTimeout z opóźnieniem, aby dać czas na animację zwijania
          setTimeout(() => {
            if (galleryRef.current) {
              const galleryTop =
                galleryRef.current.getBoundingClientRect().top + window.scrollY;
              // Przewiń do góry galerii z dodatkowym marginesem (30px), aby tytuł był dobrze widoczny
              window.scrollTo({
                top: galleryTop - 30,
                behavior: 'smooth',
              });
            }
          }, 100);
        }
      }

      setIsExpanded(expanded);
      if (onExpandChange) {
        onExpandChange(expanded);
      }
    },
    [onExpandChange],
  );

  // Sprawdź query string pdf=true i ustaw isExpanded na true
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (isPdfMode) {
        setIsExpanded(true);
        if (onExpandChange) {
          onExpandChange(true);
        }
      }
    }
  }, [isPdfMode, onExpandChange]);

  // W useEffect set var
  useEffect(() => {
    if (contentRef.current) {
      const height = contentRef.current.scrollHeight;
      contentRef.current.style.setProperty('--content-height', `${height}px`);
    }
  }, [photos, isExpanded]);

  // W collapsed state pokazujemy tylko pierwsze zdjęcia
  // W expanded state pokazujemy tylko preview (reszta jest renderowana osobno)
  const collapsedCount = isMobile ? Math.min(2, photos.length) : previewCount;
  const hasMorePhotos = photos.length > collapsedCount;
  const displayPhotos = photos.slice(0, collapsedCount);
  const visiblePhotos = isExpanded ? photos : displayPhotos;
  const getGridImageLoading = (photo: PhotoGalleryProps['photos'][number]) =>
    photo.thumbnailState ? (isPdfMode ? 'eager' : 'lazy') : undefined;

  useEffect(() => {
    visiblePhotos.forEach((photo) => photo.requestThumbnail?.());
  }, [visiblePhotos]);

  //
  // Nowa wersja z collapsible zgodnie z Figma
  return (
    <Collapsible.Root
      ref={galleryRef}
      open={isExpanded}
      onOpenChange={handleExpandChange}
      className={clsx('relative mt-10 w-full last:mb-10', className)}
      data-state={isExpanded ? 'open' : 'closed'}
    >
      {/* Container z białym tłem i shadow */}
      <div className="rounded-3xl border border-gray-300 bg-white pb-10 shadow-2xl shadow-blue-200/40">
        {/* Nagłówek z ikoną, tytułem i licznikiem */}
        {title && (
          <div className="flex flex-col gap-4 p-6 pb-0 md:flex-row md:items-center">
            {showIcon && (
              <GalleryIcon
                iconConfig={iconConfig}
                iconName={iconName}
                galleryVariant={galleryVariant}
                photosCount={photos.length}
              />
            )}
            <div className="flex flex-1 items-center gap-4">
              <div className="flex flex-col gap-1">
                <h2 className="font-archivo text-base font-semibold text-gray-900 md:ml-4 lg:text-xl">
                  {title}
                </h2>
              </div>

              {collapsible && (
                <div className="ml-auto flex items-center gap-4">
                  {collapsible && hasMorePhotos && (
                    <div className="photo-gallery-top-toggle flex items-center justify-center rounded-full bg-gold-400 p-3">
                      <Collapsible.Trigger asChild>
                        <SVGIcon
                          name="chevron-down"
                          fill="white"
                          size={'sm'}
                          className={` ${isExpanded ? 'rotate-180' : ''}`}
                        />
                      </Collapsible.Trigger>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Grid galerii zdjęć */}
        <div
          className={clsx(
            'relative overflow-hidden',
            galleryVariant === 'damage-hsp' && !isExpanded
              ? 'h-[420px] md:h-[150px]'
              : '',
          )}
          style={
            isMobile &&
            !isExpanded &&
            galleryVariant !== 'damage-hsp' &&
            hasMorePhotos
              ? { maxHeight: 'calc(100vw + 60px)' }
              : undefined
          }
        >
          <div className="p-6 pt-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              {displayPhotos.map((photo, index) => (
                <div
                  key={photo.attachmentId ?? photo.src ?? index}
                  className="group relative aspect-square cursor-pointer overflow-hidden rounded-2xl"
                  onClick={() => handlePhotoClick({ index })}
                >
                  <AuthorizedImage
                    src={photo.src}
                    alt={photo.alt}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading={getGridImageLoading(photo)}
                    onError={() => photo.refreshVariant?.('THUMBNAIL')}
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-blue opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white bg-opacity-20">
                      <SVGIcon
                        name={'search-magnify'}
                        width={24}
                        height={24}
                        fill="white"
                      />
                    </div>
                  </div>
                </div>
              ))}
              {/* Collapsible Content - dodatkowe zdjęcia */}
              {/* <Collapsible.Content
                className="overflow-hidden data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown "
                style={
                  {
                    '--radix-collapsible-content-height': `${contentRef.current?.scrollHeight}px`,
                  } as React.CSSProperties
                }
                ref={contentRef}
              > */}
              {/* <div className="px-6 pb-6"> */}
              {/* <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4"> */}
              {isExpanded &&
                photos.slice(collapsedCount).map((photo, index) => (
                  <div
                    key={photo.attachmentId ?? photo.src ?? index + collapsedCount}
                    className="group relative aspect-square cursor-pointer overflow-hidden rounded-2xl"
                    onClick={() =>
                      handlePhotoClick({ index: index + collapsedCount })
                    }
                  >
                    <AuthorizedImage
                      src={photo.src}
                      alt={photo.alt}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading={getGridImageLoading(photo)}
                      onError={() => photo.refreshVariant?.('THUMBNAIL')}
                    />
                    {/* Hover overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-blue/50 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
                        <SVGIcon
                          name="search-magnify"
                          width={24}
                          height={24}
                          fill="white"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              {/* </div> */}
              {/* </div> */}
              {/* </Collapsible.Content> */}
            </div>
            {galleryVariant === 'damage-hsp' && !isExpanded && (
              <div className="absolute bottom-0 left-0 right-0 h-[400px] w-full bg-gradient-to-b from-white/30 to-white md:h-[143px]" />
            )}
            {/* Mobile gradient for collapsed default galleries */}
            {isMobile &&
              !isExpanded &&
              galleryVariant !== 'damage-hsp' &&
              hasMorePhotos && (
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-[200px] bg-gradient-to-b from-transparent to-white" />
              )}
          </div>

          {/* Gradient mask w collapsed state (desktop) */}
          {!isMobile && !isExpanded && hasMorePhotos && (
            <div className="photo-gallery-gradient-mask" />
          )}
        </div>

        {/* Przycisk toggle na dole */}
        {collapsible && hasMorePhotos && (
          <div className="absolute bottom-[-50px] left-0 right-0 flex justify-center pb-6">
            <Collapsible.Trigger asChild>
              <Button variant="gold" className="photo-gallery-toggle-button">
                {isExpanded ? closeGalleryText : openGalleryText}
                <SVGIcon
                  name={'chevron-circle'}
                  width={20}
                  height={20}
                  fill="white"
                  className={`ml-4 ${isExpanded ? 'rotate-180' : ''}`}
                />
              </Button>
            </Collapsible.Trigger>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex >= 0 && (
        <Lightbox
          open={true}
          index={lightboxIndex}
          close={handleCloseLightbox}
          slides={lightboxSlides}
          on={{ view: handleLightboxView }}
          render={{ slide: renderLightboxSlide }}
          styles={{
            container: { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
          }}
        />
      )}
    </Collapsible.Root>
  );
};
